<template>
    <div class="sustained-note-question">
        <!-- Instructions/feedback -->
        <div class="instruction-bar">
            <div class="instruction-text">
                Hold this note for
                <strong>{{
                    formatDuration(question?.sustainDuration || 0)
                }}</strong>
            </div>
        </div>

        <!-- Progress bar for hold duration -->
        <div class="progress-container">
            <div
                class="progress-bar"
                :style="{ width: holdPercentage + '%' }"
            ></div>
        </div>

        <!-- ListeningNote component -->
        <ListeningNote
            v-if="question"
            :target-note="question.targetNote"
            :tolerance-cents="question.toleranceCents"
            :auto-start="true"
            :hide-controls="true"
            :show-fingering="question.showFingering"
            :class="{ 'status-failing': attempts > 5 }"
            @status-change="handleStatusChange"
        />

        <!-- Attempt pips -->
        <div class="attempts-display">
            <div
                v-for="pip in 5"
                :key="pip"
                class="attempt-pip"
                :class="{ used: attempts > pip }"
            ></div>
        </div>
    </div>
</template>

<script setup lang="ts">
import {
    ref,
    computed,
    onMounted,
    onUnmounted,
    PropType,
    defineOptions,
} from "vue";
import { useViewable, useQuestionView } from "@vue-skuilder/courseware";
import { ViewData } from "@vue-skuilder/common";
import ListeningNote from "../components/ListeningNote.vue";
import {
    SustainedNoteQuestion,
    type SustainedNoteAnswer,
} from "./SustainedNoteQuestion";

defineOptions({
    name: "SustainedNoteQuestionView",
});
import type { PitchDetectionResult } from "../pitch";
import type { NoteError } from "../pitch/feedbackAnalysis";

const props = defineProps({
    data: {
        type: Array as PropType<ViewData[]>,
        required: true,
    },
    modifyDifficulty: {
        type: Number,
        required: false,
        default: 0,
    },
});

const emit = defineEmits(["emitResponse"]);

// Base utilities
const viewableUtils = useViewable(props, emit, "SustainedNoteQuestionView");
const questionUtils = useQuestionView<SustainedNoteQuestion>(viewableUtils);

// State
const isHolding = ref(false);
const completed = ref(false);
const achieved = ref(false);
const attempts = ref(1);

// Sustained duration tracking
const holdStartTime = ref<number | null>(null);
const accumulatedHoldTime = ref(0);
const animationFrameId = ref<number | null>(null);

// Cents tracking for averaging
const centsOffSamples = ref<number[]>([]);

// Track last status to detect wrong note attempts
const lastStatus = ref<string>("");
const lastWrongNote = ref<string | null>(null);

// Computed
const question = computed(() => questionUtils.question.value);

const holdPercentage = computed(() => {
    if (!question.value) return 0;
    const percent =
        (accumulatedHoldTime.value / question.value.sustainDuration) * 100;
    return Math.min(100, Math.round(percent));
});

const resultClass = computed(() => {
    if (achieved.value) return "result-success";
    return "result-retry";
});

/**
 * Format duration in milliseconds to human-readable string
 */
function formatDuration(ms: number): string {
    if (ms < 1000) {
        return `${ms}ms`;
    }
    const seconds = ms / 1000;
    return `${seconds.toFixed(1)}s`;
}

/**
 * Handle status changes from ListeningNote
 */
function handleStatusChange(
    status: string,
    detectedPitch: PitchDetectionResult | null,
    noteError: NoteError | null,
) {
    if (completed.value) return;

    // Start holding when status becomes "correct"
    if (status === "correct") {
        if (!isHolding.value) {
            // Just started holding
            isHolding.value = true;
            holdStartTime.value = performance.now();
            startTrackingHold();
        }

        // Track cents off for averaging
        if (detectedPitch?.centsOff !== undefined) {
            centsOffSamples.value.push(Math.abs(detectedPitch.centsOff));
        }
    } else {
        // Status is not "correct"

        // Count as failed attempt if they played a wrong note
        if (status === "wrong" && detectedPitch?.note) {
            const currentWrongNote = detectedPitch.note;

            // Count attempt if:
            // - First wrong note detected, OR
            // - Different wrong note than last time
            if (
                lastWrongNote.value === null ||
                currentWrongNote !== lastWrongNote.value
            ) {
                lastWrongNote.value = currentWrongNote;
                attempts.value++;

                // Check failure threshold
                if (attempts.value > 5) {
                    submitFailure();
                    return;
                }
            }
        } else if (status !== "wrong") {
            // Reset wrong note tracking when not playing wrong notes
            lastWrongNote.value = null;
        }

        // Stop holding if they were holding
        if (isHolding.value) {
            // Lost the note mid-hold
            stopTrackingHold();
            resetHold();
        }
    }

    lastStatus.value = status;
}

/**
 * Start tracking sustained hold duration
 */
function startTrackingHold() {
    if (animationFrameId.value !== null) return;

    const updateHold = () => {
        if (!isHolding.value || !holdStartTime.value || !question.value) {
            return;
        }

        // Calculate accumulated hold time
        const elapsed = performance.now() - holdStartTime.value;
        accumulatedHoldTime.value = elapsed;

        // Check if we've reached the target duration
        if (accumulatedHoldTime.value >= question.value.sustainDuration) {
            completeSuccess();
            return;
        }

        // Continue tracking
        animationFrameId.value = requestAnimationFrame(updateHold);
    };

    animationFrameId.value = requestAnimationFrame(updateHold);
}

/**
 * Stop tracking hold duration
 */
function stopTrackingHold() {
    if (animationFrameId.value !== null) {
        cancelAnimationFrame(animationFrameId.value);
        animationFrameId.value = null;
    }
}

/**
 * Reset hold tracking (when note is lost)
 */
function resetHold() {
    isHolding.value = false;
    holdStartTime.value = null;
    accumulatedHoldTime.value = 0;
    centsOffSamples.value = [];
    attempts.value++;

    // Auto-fail after 5 failed attempts
    if (attempts.value > 5) {
        submitFailure();
    }
}

/**
 * Complete with success
 */
function completeSuccess() {
    stopTrackingHold();
    completed.value = true;
    achieved.value = true;

    // Calculate average cents off
    const avgCentsOff =
        centsOffSamples.value.length > 0
            ? centsOffSamples.value.reduce((a, b) => a + b, 0) /
              centsOffSamples.value.length
            : 0;

    // Submit answer
    const answer: SustainedNoteAnswer = {
        targetNote: question.value!.targetNote,
        achieved: true,
        sustainedDuration: accumulatedHoldTime.value,
        avgCentsOff: avgCentsOff,
        attempts: attempts.value,
    };

    questionUtils.submitAnswer(answer);
}

/**
 * Submit failure after too many attempts
 */
function submitFailure() {
    stopTrackingHold();
    completed.value = true;
    achieved.value = false;

    // Submit failure answer
    const answer: SustainedNoteAnswer = {
        targetNote: question.value!.targetNote,
        achieved: false,
        sustainedDuration: 0,
        avgCentsOff: 0,
        attempts: attempts.value,
    };

    questionUtils.submitAnswer(answer);
}

// Lifecycle
onMounted(() => {
    // Initialize question
    questionUtils.question.value = new SustainedNoteQuestion(props.data);
    questionUtils.maxAttemptsPerView.value = 1;
});

onUnmounted(() => {
    stopTrackingHold();
});
</script>

<style scoped>
.sustained-note-question {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 20px;
    padding: 20px;
    width: 100%;
}

/* Override border color for failing state */
.sustained-note-question :deep(.status-failing .note-container) {
    border-color: #dc3545 !important;
}

.instruction-bar {
    text-align: center;
    min-height: 50px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.instruction-text {
    font-size: 18px;
    color: #333;
}

.instruction-text strong {
    color: rgb(var(--v-theme-primary));
    font-weight: 700;
}

.holding-text {
    font-size: 20px;
    font-weight: 600;
    color: rgb(var(--v-theme-secondary));
    animation: pulse-text 1s ease-in-out infinite;
}

@keyframes pulse-text {
    0%,
    100% {
        opacity: 1;
    }
    50% {
        opacity: 0.7;
    }
}

.result-message {
    font-size: 24px;
    font-weight: 700;
    padding: 15px 30px;
    border-radius: 8px;
}

.result-success {
    color: #28a745;
    background: linear-gradient(to bottom, #f0fff4, #e6f9ea);
    border: 2px solid #28a745;
}

.result-retry {
    color: #dc3545;
    background: linear-gradient(to bottom, #fff5f5, #ffe6e6);
    border: 2px solid #dc3545;
}

.progress-container {
    width: 100%;
    max-width: 600px;
    height: 10px;
    background: #e9ecef;
    border-radius: 15px;
    overflow: hidden;
    box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.1);
}

.progress-bar {
    height: 100%;
    background: linear-gradient(90deg, rgb(var(--v-theme-accent)),rgb(var(--v-theme-secondary)));
    transition: width 0.1s linear;
    border-radius: 15px;
    position: relative;
}

.progress-bar::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
        90deg,
        rgba(255, 255, 255, 0) 0%,
        rgba(255, 255, 255, 0.3) 50%,
        rgba(255, 255, 255, 0) 100%
    );
    animation: shimmer 2s infinite;
}

.progress-bar.progress-success {
    background: linear-gradient(90deg, #28a745, #1e7e34);
}

@keyframes shimmer {
    0% {
        transform: translateX(-100%);
    }
    100% {
        transform: translateX(100%);
    }
}

.attempts-display {
    display: flex;
    justify-content: center;
    gap: 8px;
    padding: 10px 0;
}

.attempt-pip {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: rgb(var(--v-theme-accent));
    transition: all 0.3s ease;
}

.attempt-pip.used {
    background: #e9ecef;
    transform: scale(0.7);
    opacity: 0.5;
}
</style>
